import { useEffect, useMemo } from 'react';

import type { ApiListingApplicationId } from '#/api/types/ApiTypedId';
import { JobDetailsList } from '#/components/DetailsList/Job/JobDetailsList';
import { PageToolbar } from '#/components/PageToolbar/PageToolbar';
import { PrintHidden } from '#/components/PrintHidden/PrintHidden';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useCurrentUserIsStaff } from '#/store/hooks/useCurrentUserIsStaff';
import { useIsExternalUrl } from '#/hooks/useIsExternalUrl';
import { useI18n } from '#/i18n/hooks/useI18n';
import { trackViewListing } from '#/modules/listing/analytics/trackViewListing';
import { ListingViewAdminTools } from '#/modules/listing/components/ListingView/AdminTools/ListingViewAdminTools';
import { ListingViewBody } from '#/modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewExpiredTag } from '#/modules/listing/components/ListingView/ExpiredTag/ListingViewExpiredTag';
import { ListingViewFooter } from '#/modules/listing/components/ListingView/Footer/ListingViewFooter';
import { ListingViewHeader } from '#/modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from '#/modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from '#/modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from '#/modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewSignupCta } from '#/modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { ListingViewDescriptionSection } from '#/modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewBenefitsSection } from '#/modules/listing/components/ListingView/sections/ListingViewBenefitsSection';
import { ListingViewLanguageProficiencySection } from '#/modules/listing/components/ListingView/sections/ListingViewLanguageProficiencySection';
import { ListingViewLocationSection } from '#/modules/listing/components/ListingView/sections/ListingViewLocationSection';
import type { ApiJob } from '#/modules/listing/jobs/api/types/ApiJob';
import { ViewJobPageRecommendations } from '#/modules/listing/jobs/pages/view/components/Recommendations/ViewJobPageRecommendations';
import { ViewJobPageSidebar } from '#/modules/listing/jobs/pages/view/components/Sidebar/ViewJobPageSidebar';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import { SearchContextHeader } from '#/modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { useSearchSavedListingsStore } from '#/modules/search/zustand-stores/searchSavedListingsStore';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { isUserOrgOwner } from '#/utils/listings/getOrgOwner';
import { showModal } from '#/zustand-stores/modalStore';
import { dismissToast, showToast } from '#/zustand-stores/toastsStore';

type Props = {
  job: ApiJob;
  listingApplicationId: ApiListingApplicationId | null;
};

export function ExpiredJobPage({ job, listingApplicationId }: Props) {
  const { i18n, locale } = useI18n();
  const { isExternalUrl } = useIsExternalUrl();

  const applied = Boolean(listingApplicationId);

  const { user } = useUser();
  const isStaff = useCurrentUserIsStaff();
  const isOrgOwner = isUserOrgOwner(user, job.org?.id);

  const { searchContext } = useSearchContextHeader({ currentObjectId: job.id });
  const { searchSavedListingsStore: suggestionsSearchSavedListingsStore } =
    useSearchSavedListingsStore();

  const listingDetailsSearchResultsEnabled = useMemo(() => {
    if (isOrgOwner && !searchContext) return false;

    return true;
  }, [isOrgOwner, searchContext]);

  useEffect(() => {
    trackViewListing({
      status: 'expired',
      hasAts: job.hasAts,
      listingName: job.name,
      listingId: job.id,
      orgId: job.org?.id,
      orgName: job.org?.name,
      orgType: job.org?.orgType,
      listingType: 'JOB',
    });
  }, [job]);

  useEffect(() => {
    if (isStaff) return;

    const toastId = showToast({
      headline: i18n('This listing has expired'),
      body: i18n(
        'This listing has expired and is no longer accepting applications.',
      ),
      dismissible: true,
      variant: 'warning',
    });

    return () => {
      dismissToast(toastId);
    };
  }, [i18n, isStaff]);

  const orgName = job.orgNameOverride ?? job.org?.name ?? null;
  const orgUrl = job.org?.url[locale] ?? null;
  const orgType = job.org?.orgType ?? null;
  const expiredAt = Date.parse(job.expires) ? new Date(job.expires) : undefined;

  return (
    <>
      {isOrgOwner && job.org && (
        <PageToolbar
          centered
          message={i18n('Your organization posted this job')}
          tools={
            <ListingViewAdminTools
              listingId={job.id}
              listingType="JOB"
              orgId={job.org.id}
              hasAts={job.hasAts}
              canPost
            />
          }
        />
      )}

      <ListingViewBody>
        <ViewJobPageSidebar
          job={job}
          applied={applied}
          listingDetailsSearchResultsEnabled={
            listingDetailsSearchResultsEnabled
          }
          searchContext={searchContext}
          searchSavedListingsStore={suggestionsSearchSavedListingsStore}
          listingViewStatus={ListingViewStatus.EXPIRED}
        />

        <ListingViewMain>
          {searchContext && (
            <ListingViewHeaderSearchContext searchContext={searchContext} />
          )}

          <ListingViewExpiredTag expiredAt={expiredAt} isStaff={isStaff} />

          <ListingViewHeader
            address={job.address}
            isPostedAnonymously={job.isPostedAnonymously}
            listingId={job.id}
            listingName={job.name}
            listingType={job.type}
            listingFirstPublished={job.firstPublished}
            orgName={orgName}
            orgType={orgType}
            orgUrl={orgUrl}
            remoteOptions={job.remoteOptions}
            listingViewStatus={ListingViewStatus.EXPIRED}
            actionButtons={null}
          />

          <ListingViewSection title={i18n('Details')} noBorderTop>
            <JobDetailsList
              startDate={job.startDate}
              endDate={job.endDate}
              applicationDeadline={job.applicationDeadline}
              education={job.education}
              isContract={job.isContract}
              isFullTime={job.isFullTime}
              isTemporary={job.isTemporary}
              professionalLevel={job.professionalLevel}
              salaryCurrency={job.salaryCurrency}
              salaryDetails={job.salaryDetails}
              salaryMaximum={job.salaryMaximum}
              salaryMinimum={job.salaryMinimum}
              salaryPeriod={job.salaryPeriod}
              jobCountry={job.address.country}
              areasOfFocus={job.areasOfFocus}
            />
          </ListingViewSection>

          <ListingViewDescriptionSection
            listingName={job.name}
            image={null}
            description={job.description}
            descriptionTruncated={job.descriptionTruncated}
            handleShowEmailModal={(link) => {
              if (!user && isExternalUrl(link.href)) {
                showModal('EMAIL_ALERT', {
                  listingFunctions: job.functions,
                  orgId: job.org?.id,
                  listingName: job.name,
                  listingType: 'JOB',
                  synonym: job.synonym,
                });
              }
            }}
            showMoreLinkTitle={i18n('Read more about this job')}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about this listing', {
                listing_type: job.type,
                locale: job.locale,
              });
            }}
          />

          <ListingViewBenefitsSection
            benefits={job.benefits}
            benefitsTruncated={job.benefitsTruncated}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about benefits', {
                listing_type: job.type,
                locale: job.locale,
              });
            }}
          />

          <ListingViewLanguageProficiencySection
            languageProficiency={job.languageProficiency}
            languageProficiencyTruncated={job.languageProficiencyTruncated}
          />

          <ListingViewLocationSection
            listingType="JOB"
            address={job.address}
            remoteOptions={job.remoteOptions}
          />

          {searchContext ? (
            <PrintHidden>
              <div style={{ marginTop: 70 }}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </div>
            </PrintHidden>
          ) : null}
        </ListingViewMain>
      </ListingViewBody>

      <ListingViewFooter
        hiddenOnDesktop={Boolean(user && listingDetailsSearchResultsEnabled)}
      >
        <ViewJobPageRecommendations
          jobId={job.id}
          jobTitle={job.name}
          synonym={job.synonym}
          functions={job.functions}
          orgAreasOfFocus={job.org?.areasOfFocusKeys}
        />
        {!user && (
          <ListingViewSignupCta listingUrl={job.url[locale]} type={job.type} />
        )}
      </ListingViewFooter>
    </>
  );
}
